"use client";

import { useTheme } from "next-themes";
import { useEffect, useSyncExternalStore } from "react";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Screens that use the fluid toast placement and styling; see `useFluidToasts`.
let fluidScreenCount = 0;
const fluidListeners = new Set<() => void>();

function subscribeToFluidScreens(listener: () => void) {
  fluidListeners.add(listener);
  return () => {
    fluidListeners.delete(listener);
  };
}

function hasFluidScreen() {
  return fluidScreenCount > 0;
}

function changeFluidScreenCount(change: number) {
  fluidScreenCount += change;
  fluidListeners.forEach((listener) => listener());
}

/**
 * While the calling component is mounted, toasts appear at the bottom centre in the fluid
 * presenter's style. A module store rather than context, because the app's Toaster sits
 * outside the page's providers.
 */
export function useFluidToasts() {
  useEffect(() => {
    changeFluidScreenCount(1);
    return () => changeFluidScreenCount(-1);
  }, []);
}

const DEFAULT_CLASS_NAMES = {
  toast:
    "group toast !rounded-lg !font-sans group-[.toaster]:bg-card group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-md",
  description: "group-[.toast]:text-muted-foreground",
  actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
  cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
  success: "!text-success",
  error: "!text-destructive",
};

// Matches the fluid presenter's cards: rounded, bordered, foreground text with a coloured icon.
// Scoped to Sonner's styled toasts, so `toast.custom` content (such as the customer join
// toast) draws its own card.
const FLUID_CLASS_NAMES = {
  toast:
    "group toast data-[styled=true]:!rounded-xl data-[styled=true]:!border data-[styled=true]:!border-border data-[styled=true]:!bg-card data-[styled=true]:!px-4 data-[styled=true]:!py-3 !font-sans data-[styled=true]:!text-foreground data-[styled=true]:!shadow-lg data-[styled=true]:!gap-3",
  title: "!font-medium !text-sm",
  description: "!text-muted-foreground !text-xs",
  icon: "!size-5 [&_svg]:!size-5",
  actionButton: "!rounded-md !bg-primary !text-primary-foreground !text-xs !font-medium !h-8 !px-3",
  cancelButton: "!rounded-md !bg-muted !text-muted-foreground !text-xs !font-medium !h-8 !px-3",
  success: "[&_[data-icon]]:text-success",
  error: "[&_[data-icon]]:text-destructive",
  warning: "[&_[data-icon]]:text-warning",
  info: "[&_[data-icon]]:text-primary",
};

const Toaster = ({ position, ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const isFluid = useSyncExternalStore(subscribeToFluidScreens, hasFluidScreen, () => false);

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: isFluid ? FLUID_CLASS_NAMES : DEFAULT_CLASS_NAMES,
        duration: 5000,
      }}
      {...props}
      position={isFluid ? "bottom-center" : position}
    />
  );
};

export { Toaster };
